import { useEffect, useState } from "react";
import HornLakeHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

function SectionTitle({ pre, title }: { pre: string; title: string }) {
  return (
    <div className="text-center mb-10">
      <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">{pre}</p>
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">{title}</h2>
    </div>
  );
}

const faqs = [
  { q: "How fast can I get a passport in Horn Lake?", a: "We offer processing as fast as 24 hours for emergency situations. For routine expedited service, expect 3–5 business days. Standard expedited is 7–10 business days." },
  { q: "Where is the closest passport acceptance facility to Horn Lake?", a: "The Horn Lake Post Office at 401 Church Road is a USPS passport acceptance facility. However, for expedited service, we handle everything remotely — no need to visit a clerk." },
  { q: "Do you serve Memphis residents from Horn Lake?", a: "Yes. We serve the entire South Memphis metro area including Horn Lake, Southaven, Olive Branch, and Walls. Our service is fully remote regardless of which side of the state line you are on." },
  { q: "Can I renew my passport without visiting an office?", a: "If your current passport was issued within the last 15 years and you were 16 or older at the time, you can renew by mail without an office visit. We handle the expedited courier submission." },
  { q: "What do I need for a child passport?", a: "For minors under 16, both parents must appear with the child at a passport acceptance facility (or provide notarized consent), along with proof of citizenship, ID, and a passport photo." },
  { q: "Is your service cheaper than FedEx or Staples?", a: "Yes. As a registered U.S. Department of State courier — not a reseller — we offer rates typically 30–100% lower than third-party resellers like FedEx, Staples, or UPS." },
];

export default function HornLakePage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  useEffect(() => {
    document.title = "Emergency Passport Horn Lake, MS — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/horn-lake/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Horn Lake, MS — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving DeSoto County and the South Memphis metro area.");
    setMeta("geo.placename", "Horn Lake, Mississippi");
    setMeta("geo.region", "US-MS");
    setMeta("geo.position", "34.9554;-90.0348");
    setMeta("ICBM", "34.9554, -90.0348");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Mississippi", item: "https://fastpassportcenter.com/mississippi/" },
          { "@type": "ListItem", position: 3, name: "Horn Lake Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Horn Lake — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Horn Lake and DeSoto County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Horn Lake",
          containedInPlace: { "@type": "State", name: "Mississippi" },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <HornLakeHeroSection />

      {/* Sticky Nav */}
      <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-4 overflow-x-auto py-3">
          {[
            { label: "Processing", href: "#processing" },
            { label: "Services", href: "#services" },
            { label: "Local Area", href: "#local" },
            { label: "Testimonials", href: "#testimonials" },
            { label: "FAQ", href: "#faq" },
          ].map((item) => (
            <a key={item.href} href={item.href} className="text-sm font-medium text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap transition-colors">
              {item.label}
            </a>
          ))}
        </div>
      </div>

      {/* Processing Times */}
      <section id="processing" className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Processing Options" title="Horn Lake Passport Processing Times" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Emergency Same-Day", time: "24 Hours", price: "Starting at $449", best: true, icon: "ri-flashlight-line" },
              { title: "Rush Expedited", time: "3–5 Business Days", price: "Starting at $299", best: false, icon: "ri-time-line" },
              { title: "Standard Expedited", time: "7–10 Business Days", price: "Starting at $199", best: false, icon: "ri-calendar-line" },
            ].map((card, i) => (
              <div key={i} className={`bg-white border ${card.best ? "border-[#F6C344]" : "border-gray-200"} rounded-lg p-6 relative`}>
                {card.best && <span className="absolute top-4 right-4 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-2.5 py-1 rounded-full">Most Popular</span>}
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${card.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{card.title}</h3>
                <p className="text-[#F6C344] font-bold text-2xl mb-1">{card.time}</p>
                <p className="text-gray-500 text-sm mb-4">{card.price}</p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm hover:text-[#F6C344] transition-colors whitespace-nowrap">
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="What We Offer" title="Passport Services in Horn Lake, MS" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Emergency Passport", desc: "Urgent processing for travelers with immediate departure needs — as fast as 24 hours.", icon: "ri-flashlight-line" },
              { title: "Passport Renewal", desc: "Fast renewal for U.S. citizens with a passport issued within the last 15 years.", icon: "ri-refresh-line" },
              { title: "New Passport", desc: "First-time application assistance with step-by-step guidance through the process.", icon: "ri-file-add-line" },
              { title: "Lost or Stolen", desc: "Passport replacement service including DS-64 form guidance and expedited processing.", icon: "ri-error-warning-line" },
              { title: "Child Passport", desc: "Services for minors under 16 with parental consent and acceptance clerk support.", icon: "ri-parent-line" },
              { title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes.", icon: "ri-edit-line" },
              { title: "Second Passport", desc: "Second passport issuance for frequent travelers and business professionals.", icon: "ri-stack-line" },
              { title: "Damaged Passport", desc: "Replacement for water-damaged, torn, or otherwise compromised passports.", icon: "ri-first-aid-kit-line" },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area */}
      <section id="local" className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Coverage</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Serving Horn Lake &amp; the South Memphis Metro</h2>
              <p className="text-gray-500 text-base leading-relaxed mb-6">
                Horn Lake sits in the heart of DeSoto County — the fastest-growing county in Mississippi. We serve residents throughout the South Memphis metro area including <strong className="text-[#1A365D]">Southaven, Olive Branch, Walls, and Memphis, TN</strong>. Whether you are a FedEx employee with a sudden international assignment, a retail manager at the Outlets of Mississippi, or a family planning a cruise from New Orleans, we get your passport processed fast.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: "Population", value: "27,000+" },
                  { label: "County", value: "DeSoto" },
                  { label: "Nearest Airport", value: "Memphis International (MEM)" },
                  { label: "Distance to NOLA", value: "~5 hours" },
                ].map((stat, i) => (
                  <div key={i} className="bg-[#F7F8FA] rounded-lg p-4">
                    <p className="text-[#F6C344] font-bold text-lg">{stat.value}</p>
                    <p className="text-gray-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
              <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap">
                Start My Application <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Suburban%20commercial%20district%20in%20Horn%20Lake%20Mississippi%20with%20retail%20shopping%20centers%20and%20modern%20low-rise%20buildings%2C%20tree-lined%20streets%20with%20Southern%20landscaping%2C%20clear%20daytime%20sky%20with%20warm%20sunlight%2C%20clean%20suburban%20infrastructure%20with%20ample%20parking%20and%20well-maintained%20greenery%2C%20no%20readable%20text%20or%20signs&width=800&height=600&seq=horn-lake-local-img&orientation=landscape"
                alt="Horn Lake Mississippi suburban retail district"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Customer Reviews" title="What Horn Lake Travelers Say" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Marcus T.", location: "Horn Lake, MS", text: "Had a FedEx business trip to Tokyo sprung on me with 5 days notice. These guys got my passport renewed in 3 days. Absolutely saved my job.", stars: 5 },
              { name: "Jennifer K.", location: "Southaven, MS", text: "Our family cruise to Cozumel was in 2 weeks and my daughter's passport was expired. They walked us through the child passport process and got it done in time.", stars: 5 },
              { name: "David R.", location: "Olive Branch, MS", text: "Cheaper than FedEx and way faster. I got my renewal back in 4 business days. The tracking updates were super helpful.", stars: 5 },
            ].map((t, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                    <i className="ri-user-line text-[#1A365D] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[#1A365D] font-semibold text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Facilities */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Local Resources" title="Passport Acceptance Facilities Near Horn Lake" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: "Horn Lake Post Office", address: "401 Church Road, Horn Lake, MS 38637", phone: "(662) 393-6930", note: "Acceptance facility — no expedited service" },
              { name: "Southaven Main Post Office", address: "7550 Highway 51 N, Southaven, MS 38671", phone: "(662) 393-2600", note: "Full-service passport acceptance facility" },
              { name: "Olive Branch Post Office", address: "8850 Goodman Rd, Olive Branch, MS 38654", phone: "(662) 895-5120", note: "Acceptance facility — photos available" },
              { name: "DeSoto County Chancery Clerk", address: "2535 Highway 51 S, Hernando, MS 38632", phone: "(662) 469-8000", note: "County clerk — by appointment only" },
            ].map((f, i) => (
              <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white flex-shrink-0">
                    <i className="ri-map-pin-2-line text-[#1A365D]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base mb-1">{f.name}</h3>
                    <p className="text-gray-500 text-sm">{f.address}</p>
                    <p className="text-gray-500 text-sm">{f.phone}</p>
                    <p className="text-[#F6C344] text-xs font-semibold mt-1">{f.note}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-[#EEF2F8] border border-gray-200 rounded-lg p-5">
            <p className="text-[#1A365D] text-sm">
              <i className="ri-information-line text-[#F6C344] mr-2"></i>
              <strong>Important:</strong> These acceptance facilities process routine applications only. For expedited or emergency service, use our registered courier service — we handle the entire process remotely and deliver to the State Department on your behalf.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Why Choose Us" title="Fast Passport Center vs. Alternatives" />
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-4 gap-0">
              <div className="p-4 bg-[#EEF2F8] font-bold text-[#1A365D] text-sm">Feature</div>
              <div className="p-4 bg-[#1A365D] text-white font-bold text-sm text-center">Fast Passport Center</div>
              <div className="p-4 bg-gray-100 text-gray-600 font-bold text-sm text-center">FedEx / Staples</div>
              <div className="p-4 bg-gray-100 text-gray-600 font-bold text-sm text-center">DIY State Dept.</div>
            </div>
            {[
              { feature: "Fastest Processing", us: "24 hours", them1: "2–3 weeks", them2: "7–9 weeks" },
              { feature: "Registered Courier", us: "Yes", them1: "No (reseller)", them2: "N/A" },
              { feature: "Best Price Guarantee", us: "Yes", them1: "No", them2: "N/A" },
              { feature: "Phone Support", us: "Yes", them1: "Limited", them2: "No" },
              { feature: "Document Review", us: "Yes", them1: "No", them2: "No" },
              { feature: "A+ BBB Rating", us: "Yes", them1: "Varies", them2: "N/A" },
            ].map((row, i) => (
              <div key={i} className={`grid grid-cols-4 gap-0 ${i % 2 === 1 ? "bg-[#F7F8FA]" : "bg-white"}`}>
                <div className="p-4 text-[#1A365D] font-semibold text-sm">{row.feature}</div>
                <div className="p-4 text-center text-[#1A365D] font-semibold text-sm">{row.us}</div>
                <div className="p-4 text-center text-gray-500 text-sm">{row.them1}</div>
                <div className="p-4 text-center text-gray-500 text-sm">{row.them2}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mistakes */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Avoid Delays" title="Common Mistakes Horn Lake Applicants Make" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Wrong Photo Size", desc: "Many drugstore photos do not meet the exact 2x2 inch specification. We review every photo before submission.", icon: "ri-image-line" },
              { title: "Expired Check", desc: "Personal checks must be dated within the last 90 days. Expired checks cause immediate rejection.", icon: "ri-bank-card-line" },
              { title: "Missing Parental Consent", desc: "For child passports, both parents must appear or provide notarized DS-3053 consent forms.", icon: "ri-parent-line" },
              { title: "Incomplete DS-11", desc: "Leaving fields blank or using white-out on the DS-11 form results in processing delays.", icon: "ri-file-list-line" },
              { title: "Wrong Acceptance Facility", desc: "Not all post offices offer passport services. We verify the correct facility for your application type.", icon: "ri-map-pin-line" },
              { title: "Ignoring Name Changes", desc: "If your name changed since your last passport, you need supporting documents (marriage cert, court order).", icon: "ri-edit-line" },
            ].map((m, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#F7F8FA] border border-gray-200 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white flex-shrink-0">
                  <i className={`${m.icon} text-[#F6C344]`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{m.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Simple Process" title="How It Works in Horn Lake" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Apply Online", desc: "Complete our secure online application in under 5 minutes.", icon: "ri-computer-line" },
              { step: "2", title: "Send Documents", desc: "Mail your documents using our pre-paid FedEx label.", icon: "ri-send-plane-line" },
              { step: "3", title: "We Process", desc: "Our registered couriers hand-deliver to the State Department.", icon: "ri-shield-check-line" },
              { step: "4", title: "Receive Passport", desc: "Your passport arrives via secure FedEx delivery.", icon: "ri-mail-check-line" },
            ].map((step, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
                  <i className={`${step.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344] text-[#1A365D] font-bold text-sm mx-auto mb-3">{step.step}</div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Questions" title="Horn Lake Passport FAQ" />
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="font-semibold text-[#1A365D] text-sm">{faq.q}</span>
                  <i className={`${faqOpen === i ? "ri-subtract-line" : "ri-add-line"} text-[#F6C344] flex-shrink-0 ml-3`}></i>
                </button>
                {faqOpen === i && (
                  <div className="px-4 pb-4 text-gray-500 text-sm leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Resources */}
      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionTitle pre="Quick Links" title="Helpful Resources for Horn Lake Travelers" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Travel.State.Gov", desc: "Official U.S. passport information from the State Department.", href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line" },
              { title: "Memphis International Airport", desc: "Flight info and international travel requirements from MEM.", href: "https://www.flymemphis.com/", icon: "ri-plane-line" },
              { title: "TSA PreCheck", desc: "Apply for TSA PreCheck to speed through MEM security.", href: "https://www.tsa.gov/precheck", icon: "ri-shield-check-line" },
              { title: "Global Entry", desc: "Expedited customs clearance for international travelers.", href: "https://www.cbp.gov/travel/trusted-traveler-programs/global-entry", icon: "ri-earth-line" },
            ].map((r, i) => (
              <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-5 hover:border-[#F6C344] transition-all group">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/horn-lake" state="MS" />

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for Your Trip? Get Your Passport Fast — Starting at $199
          </h2>
          <p className="text-white/70 text-base mb-8 max-w-2xl mx-auto">
            Join thousands of DeSoto County travelers who trust Fast Passport Center. Emergency processing available as fast as 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Application <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}